<template>
  <modal v-if="showModal" @close="$emit('closeModal')">
    <template slot="header">
      <h4 class="modal-title">{{ title }}</h4>
      <md-button
          class="md-simple md-just-icon md-round modal-default-button"
          @click="$emit('closeModal')">
        <md-icon>clear</md-icon>
      </md-button>
    </template>

    <template slot="body">
      <div v-if="isLoading" class="loading-overlay">
        <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
      </div>

      <select-input
          v-model="activityLabourerWorkplace.workplace_id"
          deselect-label="Can't remove this value"
          :track-by="'id'"
          :appendToBody="false"
          label="Workplace"
          placeholder="Select Workplace"
          :options="filteredWorkplaces"
          :error="apiValidationErrors.workplace_id"
          :allow-empty="false"
          :disabled="isSubmitting"
          @input="onWorkplaceChange">
      </select-input>

      <div v-if="filteredWorkplaces.length === 0 && !isLoading" class="warning-box">
        <md-icon>warning</md-icon>
        <span>No workplaces available for this labourer's port and skills</span>
      </div>

      <select-input
          v-model="activityLabourerWorkplace.activity_id"
          :appendToBody="false"
          deselect-label="Can't remove this value"
          :track-by="'id'"
          label="Activity"
          placeholder="Select Activity"
          :options="filteredActivities"
          :allow-empty="false"
          :disabled="!activityLabourerWorkplace.workplace_id || isSubmitting"
          :error="apiValidationErrors.activity_id">
      </select-input>

      <div v-if="filteredActivities.length === 0 && activityLabourerWorkplace.workplace_id && !isLoading" class="warning-box">
        <md-icon>warning</md-icon>
        <span>No matching activities found for this workplace based on labourer's skills</span>
      </div>

      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-50">
          <md-field :class="{ 'md-invalid': apiValidationErrors.start_date }">
            <label>Start Date</label>
            <md-input
                v-model="activityLabourerWorkplace.start_date"
                type="datetime-local"
                :disabled="isSubmitting"
                @change="validateDates">
            </md-input>
          </md-field>
          <validation-error :errors="apiValidationErrors.start_date"/>
        </div>

        <div class="md-layout-item md-size-50">
          <md-field :class="{ 'md-invalid': apiValidationErrors.end_date }">
            <label>End Date</label>
            <md-input
                v-model="activityLabourerWorkplace.end_date"
                type="datetime-local"
                :disabled="isSubmitting"
                @change="validateDates">
            </md-input>
          </md-field>
          <validation-error :errors="apiValidationErrors.end_date"/>
        </div>
      </div>

      <div v-if="dateError" class="date-error">
        <md-icon>warning</md-icon>
        <span>{{ dateError }}</span>
      </div>

      <!-- Labourer Info Summary -->
      <div class="labourer-info" v-if="labourer">
        <div class="info-title">Labourer Information</div>
        <div class="info-row">
          <span class="info-label">Port:</span>
          <span class="info-value">{{ labourer.port_name || labourer.port?labourer.port.name: 'N/A' }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Skills:</span>
          <span class="info-value">
            <span v-if="labourerSkills && labourerSkills.length">
              <badge v-for="skill in labourerSkills" :key="skill.id" type="info" size="small" class="skill-badge">
                {{ skill.name }}
              </badge>
            </span>
            <span v-else>No skills defined</span>
          </span>
        </div>
      </div>
    </template>

    <template slot="footer">
      <md-button
          class="md-primary md-simple"
          @click="add"
          :disabled="isSubmitting || !isFormValid">
        <md-icon v-if="isSubmitting">hourglass_empty</md-icon>
        <md-icon v-else>add</md-icon>
        {{ isSubmitting ? 'Adding...' : 'Add Assignment' }}
      </md-button>
      <md-button
          class="md-danger md-simple"
          @click="$emit('closeModal')"
          :disabled="isSubmitting">
        Close
      </md-button>
    </template>
  </modal>
</template>

<script>
import {Modal} from '@/components';
import formMixin from '@/mixins/form-mixin';
import SelectInput from '@/components/Inputs/SelectInput.vue';
import ValidationError from '@/components/ValidationError.vue';
import Badge from '@/components/Badge.vue';

export default {
  name: 'AddActivityWorkplaceModal',
  components: {SelectInput, Modal, ValidationError, Badge},
  mixins: [formMixin],
  props: {
    title: {
      type: String,
      default: 'Add Activity'
    },
    labourer: {
      type: Object,
      required: true
    },
    showModal: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    model: {
      type: String,
      default: ''
    },
    modelType: {
      type: String,
      default: 'activityLabourerWorkplaces/list'
    },
    existingAssignments: {
      type: Array,
      default: function() {
        return [];
      }
    }
  },
  data() {
    return {
      selected: [],
      workplaces: [],
      labourerSkills: [],
      activityLabourerWorkplace: {},
      isLoading: false,
      isSubmitting: false,
      dateError: null
    };
  },
  computed: {
    // Filter workplaces based on labourer's port_id and skills
    filteredWorkplaces() {
      if (!this.labourer || !this.labourer.port_id) {
        console.log('No labourer or port_id found');
        return [];
      }

      // First filter by port_id
      let filtered = this.workplaces.filter(
          workplace => workplace.port_id === this.labourer.port_id
      );

      console.log('Workplaces after port filter:', filtered.length);

      // Then filter by labourer's skills (if labourer has skills)
      if (this.labourerSkills && this.labourerSkills.length) {
        const labourerSkillIds = this.labourerSkills.map(skill => skill.id);

        filtered = filtered.filter(workplace => {
          // Check if workplace has any activity that matches labourer's skills
          if (!workplace.activities || !workplace.activities.length) {
            return false;
          }

          // Check if any activity in this workplace matches labourer's skills
          const hasMatchingActivity = workplace.activities.some(activity => {
            // Check if activity requires any of labourer's skills
            if (!activity.skills || !activity.skills.length) {
              return false;
            }
            return activity.skills.some(skill => labourerSkillIds.includes(skill.id));
          });

          return hasMatchingActivity;
        });

        console.log('Workplaces after skills filter:', filtered.length);
      }

      // Filter out already assigned workplaces
      if (this.existingAssignments && this.existingAssignments.length) {
        const assignedWorkplaceIds = this.existingAssignments.map(a => a.workplace_id);
        filtered = filtered.filter(w => !assignedWorkplaceIds.includes(w.id));
      }

      return filtered;
    },

    // Filter activities based on labourer's skills
    filteredActivities() {
      if (!this.activityLabourerWorkplace.workplace_id) {
        return [];
      }

      const selectedWorkplace = this.workplaces.find(
          workplace => workplace.id === this.activityLabourerWorkplace.workplace_id
      );

      if (!selectedWorkplace || !selectedWorkplace.activities) {
        return [];
      }

      let activities = [...selectedWorkplace.activities];

      // Filter activities based on labourer's skills
      if (this.labourerSkills && this.labourerSkills.length) {
        const labourerSkillIds = this.labourerSkills.map(skill => skill.id);

        activities = activities.filter(activity => {
          // If activity has no skills defined, it might be available to all
          if (!activity.skills || !activity.skills.length) {
            return true; // Or false depending on business logic
          }

          // Check if activity requires any of labourer's skills
          return activity.skills.some(skill => labourerSkillIds.includes(skill.id));
        });

        console.log('Activities after skills filter:', activities.length);
      }

      // Filter out already assigned activities for this workplace
      if (this.existingAssignments && this.existingAssignments.length) {
        const assignedActivities = this.existingAssignments
            .filter(a => a.workplace_id === this.activityLabourerWorkplace.workplace_id)
            .map(a => a.activity_id);

        activities = activities.filter(a => !assignedActivities.includes(a.id));
      }

      return activities;
    },

    isFormValid() {
      return this.activityLabourerWorkplace.workplace_id &&
          this.activityLabourerWorkplace.activity_id &&
          this.activityLabourerWorkplace.start_date &&
          !this.dateError &&
          !this.isSubmitting;
    }
  },
  watch: {
    showModal: {
      handler: function(val) {
        if (val) {
          this.resetActivityLabourerWorkplace();
          this.fetchLabourerSkills();
          this.fetchData();
          this.clearErrors();
        }
      }
    }
  },
  created() {
    this.resetActivityLabourerWorkplace();
  },
  methods: {
    onWorkplaceChange() {
      // Reset activity selection when workplace changes
      this.activityLabourerWorkplace.activity_id = '';
      // Clear any validation errors for activity
      if (this.apiValidationErrors.activity_id) {
        this.apiValidationErrors.activity_id = null;
      }
      this.validateDates();
    },

    validateDates() {
      this.dateError = null;

      const startDate = this.activityLabourerWorkplace.start_date;
      const endDate = this.activityLabourerWorkplace.end_date;

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (end < start) {
          this.dateError = 'End date cannot be before start date';
        }

        // Optional: Check against guidance dates if available
        if (this.labourer.guidance_start_date) {
          const guidanceStart = new Date(this.labourer.guidance_start_date);
          const guidanceEnd = new Date(this.labourer.guidance_end_date);

          if (start < guidanceStart) {
            this.dateError = 'Start date cannot be before guidance start date';
          }
          if (end > guidanceEnd) {
            this.dateError = 'End date cannot be after guidance end date';
          }
        }
      }

      return !this.dateError;
    },

    clearErrors() {
      this.dateError = null;
      if (this.apiValidationErrors) {
        this.apiValidationErrors.workplace_id = null;
        this.apiValidationErrors.activity_id = null;
        this.apiValidationErrors.start_date = null;
        this.apiValidationErrors.end_date = null;
      }
    },

    async add() {
      if (!this.isFormValid) {
        if (!this.activityLabourerWorkplace.workplace_id) {
          this.notifyWarning('Please select a workplace');
        } else if (!this.activityLabourerWorkplace.activity_id) {
          this.notifyWarning('Please select an activity');
        } else if (!this.activityLabourerWorkplace.start_date) {
          this.notifyWarning('Please select a start date');
        } else if (this.dateError) {
          this.notifyWarning(this.dateError);
        }
        return;
      }

      this.isSubmitting = true;

      try {

        const formattedStart = this.activityLabourerWorkplace.start_date
            ? this.activityLabourerWorkplace.start_date.replace('T', ' ') + ':00'
            : null;

        const formattedEnd = this.activityLabourerWorkplace.end_date
            ? this.activityLabourerWorkplace.end_date.replace('T', ' ') + ':00'
            : null;


        const payload = {
          labourer_id: this.labourer.id,
          workplace_id: this.activityLabourerWorkplace.workplace_id,
          activity_id: this.activityLabourerWorkplace.activity_id,
          start_date: formattedStart,
          end_date: formattedEnd,
          type: 'activityLabourerWorkplaces'
        };

        await this.$store.dispatch('activityLabourerWorkplaces/add', payload);
        const saved = this.$store.getters['activityLabourerWorkplaces/activityLabourerWorkplace'];

        this.notifySuccess('Workplace and Activity assigned successfully.');
        this.$emit('addActivityLabourerWorkplaces', saved);
        this.$emit('closeModal');
        this.resetActivityLabourerWorkplace();

      } catch (e) {
        console.error('Error adding assignment:', e);
        this.notifyException(e);
      } finally {
        this.isSubmitting = false;
      }
    },

    async fetchLabourerSkills() {
      if (!this.labourer || !this.labourer.id) return;

      try {
        await this.$store.dispatch('labourers/get', this.labourer.id);
        const labourerData = this.$store.getters['labourers/labourer'];
        this.labourerSkills = labourerData?labourerData.skills : [];

        console.log('Labourer skills loaded:', this.labourerSkills);

      } catch (error) {
        console.error('Error fetching labourer skills:', error);
        this.labourerSkills = [];
      }
    },

    async fetchData() {
      this.isLoading = true;

      try {
        // Fetch workplaces with their activities and skills
        const workplaceParam = {
          include: 'activities.skills'
        };
        await this.$store.dispatch('workplaces/list', workplaceParam);
        this.workplaces = this.$store.getters['workplaces/list'] || [];

        console.log('Loaded workplaces with activities:', this.workplaces);

      } catch (error) {
        console.error('Error fetching workplaces:', error);
        this.notifyError('Failed to load workplaces');
      } finally {
        this.isLoading = false;
      }
    },

    resetActivityLabourerWorkplace() {
      this.activityLabourerWorkplace = {
        labourer_id: this.labourer ? this.labourer.id : null,
        type: 'activityLabourerWorkplaces',
        workplace_id: '',
        activity_id: '',
        start_date: '',
        end_date: ''
      };
      this.dateError = null;
      this.isSubmitting = false;
    }
  }
};
</script>

<style scoped>
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 4px;
}

.warning-box {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff3e0;
  border-left: 4px solid #ff9800;
  padding: 12px;
  margin: 12px 0;
  color: #e65100;
  font-size: 13px;
  border-radius: 4px;
}

.warning-box md-icon {
  color: #ff9800;
}

.date-error {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #f44336;
  font-size: 12px;
  margin-top: 8px;
  padding: 8px;
  background: #ffebee;
  border-radius: 4px;
}

.date-error md-icon {
  font-size: 16px;
}

.labourer-info {
  margin-top: 16px;
  padding: 12px;
  background: #f5f5f5;
  border-radius: 8px;
}

.info-title {
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
  font-size: 13px;
}

.info-row {
  display: flex;
  margin-bottom: 6px;
  font-size: 12px;
}

.info-label {
  width: 80px;
  color: #666;
  font-weight: 500;
}

.info-value {
  flex: 1;
  color: #333;
}

.skill-badge {
  margin-right: 4px;
  margin-bottom: 4px;
  display: inline-block;
}

/* Disabled state styles */
.md-button[disabled] {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .md-layout .md-layout-item {
    width: 100%;
  }
}
</style>